import React, { useEffect, useState } from "react";
import {
  Link,
  LinkSizeEnum,
} from "../../../../../../components/core/Link/Link";
import {
  AlignmentEnum,
  FontWeightEnum,
} from "../../../../../../types/AppTypes";
import {
  COMPETENCE_LABEL,
  DELETE_LANGUAGE_CONFIRM,
  DELETE_LANGUAGE_LABEL,
  EMPTY_SKILL_LANGUAGE_MESSAGE,
  EMPTY_TECH_SKILLS_MESSAGE,
  LANGUAGE_LABEL,
  LANGUAGE_SKILL_LABEL,
  LANGUAGE_SKILLS_LABEL,
} from "../../../../../../constants/Dictionary";
import { ReactComponent as AddIcon } from "../../../../../../icons/add.svg";
import { EmptyState } from "../../../../../../components/EmptyState/EmptyState";
import emptyLanguageImage from "../../../../../../images/language.png";
import {
  Button,
  ButtonVariants,
} from "../../../../../../components/core/Button/Button";
import { Loader } from "../../../../../../components/core/Loader/Loader";
import { DoubleStructure } from "../../../../../../components/core/DoubleStructure/DoubleStructure";
import { BodyTyp } from "../../../../../../components/Typography/Typography";
import SearchInput from "../../../../../../components/core/SearchInput/SearchInput";
import { StyledExpandTable } from "../../ProfessionalHistory.styled";
import {
  Table,
  TableThemeEnum,
} from "../../../../../../components/core/Table/Table";
import { getCollaboratorHistorySkillsLanguageColumns } from "../../../../../../constants/TableColumns";
import {
  CollaboratorCmSheet,
  CollaboratorDetails,
  CollaboratorsHistorySkillLanguages,
} from "../../../../../../types/api/collaborators.type";
import { StyledLanguages } from "./LanguagesHistory.styled";
import { LanguageSkillsModal } from "./LanguageSkillsModal/LanguageSkillsModal";
import { PopupConfirm } from "../../../../../../components/PopupConfirm/PopupConfirm";
import { useAppSelector } from "../../../../../../hooks/store.hook";
import { useDebounce } from "use-debounce";
import { findLabelFromValue } from "../../../../../../utils/orgFunctions.utils";
import LoadingThumbnail from "../../../../../../components/LoadingThumbnail/LoadingThumbnail";
import { canEditCollaboratorHistory } from "../../../../../../utils/canEditCollaboratorHistory.utils";
import {
  useHistoryLanguage,
  useHistoryLanguageDelete,
} from "../../../../../../hooks/professionalHistory.hook";

type LanguagesHistoryProps = {
  setLink: (component: React.ReactNode) => void;
  collaborator: CollaboratorDetails;
  onStatusRefresh: () => void;
  isMyProfile?: boolean;
  setHistoryRefetchFn?: (fn: () => void) => void;
  currentCmSheet?: CollaboratorCmSheet;
};

export const LanguagesHistory = ({
  setLink,
  collaborator,
  onStatusRefresh,
  isMyProfile,
  currentCmSheet,
}: LanguagesHistoryProps) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] =
    useState<CollaboratorsHistorySkillLanguages | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 200);

  const { profile } = useAppSelector((state) => state.user);
  const { orgSettings } = useAppSelector((state) => state.config);
  const profileId = collaborator.id;
  const {
    data: historyLanguages,
    isLoading: loadingSkillsLanguage,
    isFetching: fetchingSkillsLanguage,
  } = useHistoryLanguage(profileId);
  const { mutateAsync: deleteHistoryLanguage, isPending } =
    useHistoryLanguageDelete(profileId);

  const hasData =
    historyLanguages !== undefined ? historyLanguages.length > 0 : null;

  const canEditHistory = canEditCollaboratorHistory(
    profile,
    collaborator,
    currentCmSheet,
    isMyProfile,
  );

  useEffect(() => {
    if (hasData && canEditHistory) {
      setLink(
        <Link
          onClick={() => {
            setShowAddModal(true);
          }}
          icon={<AddIcon />}
          size={LinkSizeEnum.SMALL}
        >
          {COMPETENCE_LABEL}
        </Link>,
      );
    }

    return () => setLink(null);
  }, [hasData, canEditHistory]);

  const onEdit = (record: CollaboratorsHistorySkillLanguages) => {
    setSelectedLanguage(record);
    setShowAddModal(true);
  };

  const onDelete = (record: CollaboratorsHistorySkillLanguages) => {
    setSelectedLanguage(record);
    setShowDeleteModal(true);
  };

  const hideDeletePopup = () => {
    setSelectedLanguage(null);
    setShowDeleteModal(false);
  };

  const handleDeleteLanguage = async () => {
    if (!selectedLanguage) {
      return null;
    }

    await deleteHistoryLanguage(selectedLanguage.id);
    hideDeletePopup();
    onStatusRefresh();
  };

  const filteredLanguageList = searchDebouncedValue
    ? historyLanguages?.filter((item) => {
        return findLabelFromValue(
          orgSettings.languageSkills.languages,
          item.language,
        )
          .toLowerCase()
          .includes(searchDebouncedValue.toLocaleLowerCase());
      })
    : historyLanguages;

  const totalLanguages = filteredLanguageList?.length ?? 0;

  const userLanguage = historyLanguages?.map((l) => l.language);

  const hideModal = () => {
    setSelectedLanguage(null);
    setShowAddModal(false);
  };

  const handleSave = async () => {
    hideModal();
    onStatusRefresh();
  };

  return (
    <>
      <StyledLanguages>
        {loadingSkillsLanguage && hasData === null ? (
          <Loader loading>
            <LoadingThumbnail marginTop={316} />
          </Loader>
        ) : (
          <>
            {!hasData && !fetchingSkillsLanguage ? (
              <div className="empty-state">
                <EmptyState
                  label={EMPTY_SKILL_LANGUAGE_MESSAGE}
                  imageSrc={emptyLanguageImage}
                >
                  {canEditHistory && (
                    <Button
                      label={LANGUAGE_SKILL_LABEL}
                      variant={ButtonVariants.SECONDARY}
                      icon={<AddIcon />}
                      onClick={() => {
                        setShowAddModal(true);
                      }}
                    />
                  )}
                </EmptyState>
              </div>
            ) : (
              <>
                <DoubleStructure verticalAlignment={AlignmentEnum.CENTER}>
                  <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                    {`${totalLanguages} ${totalLanguages === 1 ? LANGUAGE_SKILL_LABEL : LANGUAGE_SKILLS_LABEL}`}
                  </BodyTyp>
                  <div className="filters">
                    <SearchInput
                      onChange={setSearchValue}
                      label={LANGUAGE_LABEL}
                      name={"search"}
                      value={searchValue}
                    />
                  </div>
                </DoubleStructure>
                <StyledExpandTable>
                  <Table<CollaboratorsHistorySkillLanguages>
                    theme={TableThemeEnum.DARK}
                    loading={fetchingSkillsLanguage}
                    rowKey={"id"}
                    headers={getCollaboratorHistorySkillsLanguageColumns(
                      orgSettings,
                    )}
                    scrollY={"calc(100dvh - 502px)"}
                    data={filteredLanguageList ?? []}
                    emptyText={EMPTY_TECH_SKILLS_MESSAGE}
                    onEdit={canEditHistory ? onEdit : undefined}
                    onDelete={canEditHistory ? onDelete : undefined}
                  />
                </StyledExpandTable>
              </>
            )}
          </>
        )}
      </StyledLanguages>
      <LanguageSkillsModal
        record={selectedLanguage}
        isOpen={showAddModal}
        onSave={handleSave}
        onClose={() => hideModal()}
        {...(selectedLanguage && {
          record: selectedLanguage,
        })}
        userLanguage={userLanguage}
        collaborator={collaborator}
      />
      {selectedLanguage ? (
        <PopupConfirm
          isOpen={showDeleteModal}
          label={DELETE_LANGUAGE_LABEL}
          popupText={`${DELETE_LANGUAGE_CONFIRM} ${findLabelFromValue(
            orgSettings.languageSkills.languages,
            selectedLanguage?.language,
          )}?`}
          onConfirm={() => {
            handleDeleteLanguage();
          }}
          onCancel={() => {
            hideDeletePopup();
          }}
          isLoading={isPending}
        />
      ) : null}
    </>
  );
};
